// components/organization/ExamCard.jsx
import React, { useMemo } from "react";
import {
  CalendarDays,
  Clock,
  FileText,
  Layers,
  BadgeCheck,
  FileCog2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

function stripHtml(s = "") {
  return String(s).replace(/<[^>]*>/g, "");
}

function fmtMinutes(min) {
  const n = Number(min) || 0;
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
  } catch {
    return "—";
  }
}

const ExamCard = ({ exam, onManage, onView }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  // title/description با توجه به زبان
  const title = useMemo(() => {
    if (!exam?.title) return t("exam_card.untitled") || "Untitled exam";
    return exam.title.fa || exam.title.en || String(exam.title);
  }, [exam, t]);

  const description = useMemo(() => {
    const raw =
      exam?.description?.fa ||
      exam?.description?.en ||
      (typeof exam?.description === "string" ? exam.description : "");
    const clean = stripHtml(raw).trim();
    return clean || t("exam_card.no_description") || "No description";
  }, [exam, t]);

  const isPublished = !!exam?.isPublished;
  const statusPill = isPublished
    ? {
        text: t("exam_card.status_published") || "Published",
        classes: "bg-green-100 text-green-700",
      }
    : {
        text: t("exam_card.status_draft") || "Draft",
        classes: "bg-amber-100 text-amber-700",
      };

  const catTitle = exam?.category?.title || "";
  const catLevel = exam?.category?.level ? `L${exam.category.level}` : "";
  const timeMin = Number(exam?.totalTimeMin) || 0;
  const itemsCount = Number(exam?.itemsCount) || 0;
  const sectionCount = Number(exam?.sectionCount) || 0;
  const when = exam?.updatedAt || exam?.createdAt;

  // RTL helpers
  const flexRow = isRtl ? "flex-row" : "flex-row-reverse";

  return (
    <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg hover:border-indigo-300 transition">
      {/* Header */}
      <div className={`flex items-start justify-between ${flexRow} gap-3 mb-2`}>
        <div className="min-w-0">
          <h3
            className="text-lg font-bold text-gray-900 truncate"
            title={title}
          >
            {title}
          </h3>
          {/* دسته‌بندی */}
          {exam?.category && (
            <div
              className={`mt-1 text-xs text-gray-600 flex items-center gap-2 ${flexRow}`}
            >
              <Layers size={14} className="text-gray-400" />
              <span className="truncate">
                {t("exam_card.category") || "Category"}: {catTitle}
                {catLevel ? ` • ${catLevel}` : ""}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-1">
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusPill.classes}`}
          >
            {statusPill.text}
          </span>
          {/* اگر خواستی Badge سطح/برچسب دیگری هم اضافه کن */}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 line-clamp-3 mb-3">{description}</p>

      {/* Meta grid */}
      <div
        className={`grid grid-cols-2 gap-y-2 gap-x-4 text-sm border-y border-gray-100 py-3 mb-4 ${
          isRtl ? "text-right" : "text-left"
        }`}
      >
        <div className={`flex items-center gap-2 ${flexRow}`}>
          <CalendarDays size={16} className="text-gray-500" />
          <span className="text-gray-700">{fmtDate(when)}</span>
        </div>

        <div className={`flex items-center gap-2 ${flexRow}`}>
          <Clock size={16} className="text-gray-500" />
          <span className="text-orange-700 font-medium">
            {fmtMinutes(timeMin)}
          </span>
        </div>

        <div className={`flex items-center gap-2 ${flexRow}`}>
          <FileText size={16} className="text-gray-500" />
          <span className="text-purple-700 font-medium">
            {t("exam_card.question_count", { count: itemsCount }) ||
              `Questions: ${itemsCount}`}
          </span>
        </div>

        <div className={`flex items-center gap-2 ${flexRow}`}>
          <FileCog2 size={16} className="text-gray-500" />
          <span className="text-gray-700">
            {t("exam_card.section_count", { count: sectionCount }) ||
              `Sections: ${sectionCount}`}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div
        className={`mt-auto flex gap-2 ${
          isRtl ? "justify-start" : "justify-end"
        }`}
      >
        {onManage && (
          <button
            onClick={() => onManage(exam)}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
          >
            {t("exam_card.manage_button") || "Manage"}
          </button>
        )}
        {onView && (
          <button
            onClick={() => onView(exam)}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition"
          >
            {t("exam_card.view_button") || "View"}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExamCard;
